import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

type AccountType = 'client' | 'distributeur' | null;
type ClientType = 'particulier' | 'clinique' | 'hopital' | 'laboratoire' | 'autre';

export default function InscriptionPage() {
  const [accountType, setAccountType] = useState<AccountType>(null);
  const [clientType, setClientType] = useState<ClientType>('particulier');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    telephone: '',
    nomArabe: '',
    nomFrancais: '',
    wilaya: '',
    ville: '',
    adresse: '',
    message: '',
    raisonSociale: '',
    rc: '',
    nif: '',
    activite: '',
    acceptConditions: false
  });

  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar',
    'Blida', 'Bouira', 'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger',
    'Djelfa', 'Jijel', 'Sétif', 'Saïda', 'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma',
    'Constantine', 'Médéa', 'Mostaganem', 'M\'Sila', 'Mascara', 'Ouargla', 'Oran', 'El Bayadh',
    'Illizi', 'Bordj Bou Arreridj', 'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued',
    'Khenchela', 'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent',
    'Ghardaïa', 'Relizane', 'Timimoun', 'Bordj Badji Mokhtar', 'Ouled Djellal', 'Béni Abbès',
    'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El M\'Ghair', 'El Meniaa'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accountType) {
      alert('Veuillez sélectionner un type de demande');
      return;
    }

    if (!formData.acceptConditions) {
      alert('Veuillez accepter les conditions d\'utilisation');
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToInsert: any = {
        type_demande: accountType,
        email: formData.email,
        telephone: formData.telephone,
        nom_arabe: formData.nomArabe,
        nom_francais: formData.nomFrancais,
        wilaya: formData.wilaya,
        ville: formData.ville,
        adresse: formData.adresse || null,
      };

      if (accountType === 'client') {
        dataToInsert.type_client = clientType;
        dataToInsert.message = formData.message || null;
      } else {
        dataToInsert.raison_sociale = formData.raisonSociale;
        dataToInsert.rc = formData.rc;
        dataToInsert.nif = formData.nif;
        dataToInsert.activite = formData.activite;
        
        const form = e.target as HTMLFormElement;
        const fileInput = form.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput?.files?.[0]) {
          dataToInsert.document = fileInput.files[0].name;
        }
      }

      const { error } = await supabase
        .from('inscription_requests')
        .insert([dataToInsert]);

      if (error) {
        console.error('Error inserting data:', error);
        alert('Une erreur est survenue. Veuillez réessayer.');
        return;
      }

      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-white pt-20">
          <div className="bg-[#E31B23] text-white py-16 sm:py-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-checkbox-circle-line text-4xl sm:text-5xl"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Demande envoyée avec succès</h1>
              <p className="text-lg sm:text-xl text-white/90">Votre demande d'inscription a été transmise à notre équipe</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 sm:p-8 rounded-lg mb-8 sm:mb-12">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-xl sm:text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">Traitement de votre demande</h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">
                    Votre demande d'inscription a été transmise à l'équipe administrative de SARL ADF Biomédical.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">
                    Après vérification et validation, un nom d'utilisateur et un mot de passe vous seront envoyés par e-mail.
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed font-semibold">
                    Sans validation, aucun accès ne sera accordé.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg text-center">
              <i className="ri-time-line text-4xl sm:text-5xl text-[#E31B23] mb-3 sm:mb-4"></i>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">Délai de traitement</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
                Notre équipe traitera votre demande dans les plus brefs délais. Vous recevrez une réponse par e-mail sous 48 à 72 heures ouvrables.
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-[#C41820] transition-colors whitespace-nowrap text-sm sm:text-base"
              >
                Retour à l'accueil
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <div className="bg-[#E31B23] text-white py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <i className="ri-user-add-line text-4xl sm:text-5xl"></i>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">Demande d'inscription</h1>
            <p className="text-lg sm:text-xl text-white/90">Rejoignez le réseau A.D.F Biomedical</p>
          </div>
        </div>

        {/* Form Section */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <form onSubmit={handleSubmit} data-readdy-form id="inscription-form">
            {/* Type de demande */}
            <div className="mb-10 sm:mb-12">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Type de demande</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                <button
                  type="button"
                  onClick={() => setAccountType('client')}
                  className={`p-6 sm:p-8 rounded-xl border-2 transition-all text-left ${
                    accountType === 'client'
                      ? 'border-[#E31B23] bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#E31B23] rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-user-line text-2xl sm:text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Demande Client</h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Pour les particuliers, cliniques, hôpitaux, laboratoires et établissements de santé
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setAccountType('distributeur')}
                  className={`p-6 sm:p-8 rounded-xl border-2 transition-all text-left ${
                    accountType === 'distributeur'
                      ? 'border-[#E31B23] bg-red-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#E31B23] rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <i className="ri-building-line text-2xl sm:text-3xl text-white"></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Demande Distributeur</h3>
                  <p className="text-sm sm:text-base text-gray-600">
                    Pour les distributeurs, grossistes et partenaires commerciaux
                  </p>
                </button>
              </div>
            </div>

            {accountType && (
              <>
                {/* Informations de contact */}
                <div className="mb-10 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">Informations de contact</h2>
                  <div className="space-y-5 sm:space-y-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Adresse e-mail professionnelle <span className="text-[#E31B23]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="exemple@email.com"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Numéro de téléphone <span className="text-[#E31B23]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        required
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        placeholder="+213 XXX XXX XXX"
                        pattern="^\+213[0-9\s]{9,}$"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations identité */}
                <div className="mb-10 sm:mb-12">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-6">
                    {accountType === 'client' ? 'Informations personnelles' : 'Informations société'}
                  </h2>
                  <div className="space-y-5 sm:space-y-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Nom complet en arabe <span className="text-[#E31B23]">*</span>
                      </label>
                      <input
                        type="text"
                        name="nom_arabe"
                        required
                        value={formData.nomArabe}
                        onChange={(e) => setFormData({ ...formData, nomArabe: e.target.value })}
                        placeholder="أحمد محمد"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                        dir="rtl"
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        Nom complet en français <span className="text-[#E31B23]">*</span>
                      </label>
                      <input
                        type="text"
                        name="nom_francais"
                        required
                        value={formData.nomFrancais}
                        onChange={(e) => setFormData({ ...formData, nomFrancais: e.target.value })}
                        placeholder="Ahmed Mohamed"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>

                    {accountType === 'client' && (
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Type de client <span className="text-[#E31B23]">*</span>
                        </label>
                        <select
                          name="type_client"
                          required
                          value={clientType}
                          onChange={(e) => setClientType(e.target.value as ClientType)}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                        >
                          <option value="particulier">Particulier</option>
                          <option value="clinique">Clinique</option>
                          <option value="hopital">Hôpital</option>
                          <option value="laboratoire">Laboratoire</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                    )}

                    {accountType === 'distributeur' && (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Raison sociale <span className="text-[#E31B23]">*</span>
                          </label>
                          <input
                            type="text"
                            name="raison_sociale"
                            required
                            value={formData.raisonSociale}
                            onChange={(e) => setFormData({ ...formData, raisonSociale: e.target.value })}
                            placeholder="SARL Exemple"
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                              Numéro du registre de commerce (RC) <span className="text-[#E31B23]">*</span>
                            </label>
                            <input
                              type="text"
                              name="rc"
                              required
                              value={formData.rc}
                              onChange={(e) => setFormData({ ...formData, rc: e.target.value })}
                              placeholder="00/00-0000000"
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                              Numéro d'identification fiscale (NIF) <span className="text-[#E31B23]">*</span>
                            </label>
                            <input
                              type="text"
                              name="nif"
                              required
                              value={formData.nif}
                              onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                              placeholder="000000000000000"
                              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Wilaya <span className="text-[#E31B23]">*</span>
                        </label>
                        <select
                          name="wilaya"
                          required
                          value={formData.wilaya}
                          onChange={(e) => setFormData({ ...formData, wilaya: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                        >
                          <option value="">Sélectionner une wilaya</option>
                          {wilayas.map((wilaya) => (
                            <option key={wilaya} value={wilaya}>{wilaya}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Ville <span className="text-[#E31B23]">*</span>
                        </label>
                        <input
                          type="text"
                          name="ville"
                          required
                          value={formData.ville}
                          onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                          placeholder="Nom de la ville"
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {accountType === 'client' ? 'Adresse complète' : 'Adresse du siège'}
                      </label>
                      <input
                        type="text"
                        name={accountType === 'client' ? 'adresse' : 'adresse_siege'}
                        value={formData.adresse}
                        onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                        placeholder="Rue, quartier, commune"
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>

                    {accountType === 'distributeur' && (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Activité principale <span className="text-[#E31B23]">*</span>
                          </label>
                          <input
                            type="text"
                            name="activite"
                            required
                            value={formData.activite}
                            onChange={(e) => setFormData({ ...formData, activite: e.target.value })}
                            placeholder="Distribution de dispositifs médicaux"
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                            Document justificatif <span className="text-[#E31B23]">*</span>
                          </label>
                          <p className="text-sm text-gray-500 mb-2">Extrait RC ou agrément (PDF ou JPG, max 5 Mo)</p>
                          <input
                            type="file"
                            name="document"
                            required
                            accept=".pdf,.jpg,.jpeg"
                            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E31B23] file:text-white file:cursor-pointer hover:file:bg-[#C41820]"
                          />
                        </div>
                      </>
                    )}

                    {accountType === 'client' && (
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                          Message (optionnel)
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Précisez votre besoin ou type de produits recherchés"
                          rows={4}
                          maxLength={500}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E31B23] focus:border-transparent text-xs sm:text-sm resize-none"
                        />
                        <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 caractères</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conditions */}
                <div className="mb-6 sm:mb-8">
                  <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="accept_conditions"
                      required
                      checked={formData.acceptConditions}
                      onChange={(e) => setFormData({ ...formData, acceptConditions: e.target.checked })}
                      className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-[#E31B23] border-gray-300 rounded focus:ring-[#E31B23]"
                    />
                    <span className="text-xs sm:text-sm text-gray-700">
                      J'accepte les{' '}
                      <Link to="/legal/conditions-utilisation" className="text-[#E31B23] hover:underline">
                        conditions d'utilisation
                      </Link>{' '}
                      et la{' '}
                      <Link to="/legal/politique-confidentialite" className="text-[#E31B23] hover:underline">
                        politique de protection des données
                      </Link>
                      . <span className="text-[#E31B23]">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#E31B23] text-white py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg hover:bg-[#C41820] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande d\'inscription'}
                </button>

                {/* Info Box */}
                <div className="mt-6 sm:mt-8 bg-blue-50 border-l-4 border-blue-500 p-5 sm:p-6 rounded-lg">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <i className="ri-information-line text-xl sm:text-2xl text-blue-500 flex-shrink-0"></i>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Traitement de votre demande</h3>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Votre demande d'inscription sera transmise à l'équipe administrative de SARL ADF Biomédical. 
                        Après vérification et validation, un nom d'utilisateur et un mot de passe vous seront envoyés par e-mail. 
                        Sans validation, aucun accès ne sera accordé.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
